import { AuthResponse, User } from './IAuth';
import { ApiLocalStorage } from '../utils/ApiLocalStorage';
import { apiUserAccount } from '../utils/ApiService';
import { ApiEndpoints } from '../modules/account/constantes/ApiEndpoints';
import { SCHEMA_STORAGE_KEY } from 'modules/account/services/SchemaService';

class UserManager {
  events = {
    userLoadedCallbacks: [] as Array<(user: User) => void>,
    userUnloadedCallbacks: [] as Array<() => void>,
    
    addUserLoaded(callback: (user: User) => void): void {
      this.userLoadedCallbacks.push(callback);
    },
    
    addUserUnloaded(callback: () => void): void {
      this.userUnloadedCallbacks.push(callback);
    },
    
    removeUserLoaded(callback: (user: User) => void): void {
      this.userLoadedCallbacks = this.userLoadedCallbacks.filter(cb => cb !== callback);
    },
    
    removeUserUnloaded(callback: () => void): void {
      this.userUnloadedCallbacks = this.userUnloadedCallbacks.filter(cb => cb !== callback);
    },
    
    triggerUserLoaded(user: User): void {
      this.userLoadedCallbacks.forEach(callback => callback(user));
    },
    
    triggerUserUnloaded(): void {
      this.userUnloadedCallbacks.forEach(callback => callback());
    }
  };
  private user: User | null = null;
  
  async setUser(username: string, accessToken: string, expiresIn: string) {
    try {
      const user = await apiUserAccount.get<AuthResponse>(`${ApiEndpoints.account.getUserMeData}?schemaId=${ApiLocalStorage.get(SCHEMA_STORAGE_KEY).id}`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`
          }
        }) as any;
      
      this.user = {
        profile: {
          name: user.fullname,
          ...user
        },
        access_token: accessToken,
        id_token: accessToken,
        expires_in: expiresIn,
        status: 'active',
        updated_at: new Date().toISOString()
      };
      
    } catch (error) {
      console.error('Erro ao obter dados do usuário no setUser:', error);
      throw error;
    }
  }
  
  async login(username: string, password: string, token: string): Promise<User> {
    
    if (password.includes('accessToken')) {
      const decode = JSON.parse(password);
      
      return this.loginOtp(username, decode.accessToken, decode.expiresIn);
    }
    
    const data = await apiUserAccount.post<AuthResponse>(ApiEndpoints.auth.password, {
      username,
      password
    },
    {
      headers: {
        'X-Challenge-Verify': token,
        'X-Challenge-Host': process.env.REACT_APP_TURNSTILE_HOST
      }
    }) as any;
    
    await this.setUser(username, data.accessToken, data.expiresIn);
    
    if (!this.user) {
      throw new Error('User not initialized');
    }
    
    ApiLocalStorage.set('user', this.user);
    
    const expirationTime = new Date().getTime() + 55 * 60 * 1000;
    ApiLocalStorage.set('session-expiration', expirationTime);
    
    this.events.triggerUserLoaded(this.user);
    
    return this.user;
  }
  
  async loginOtp(username: string, accessToken: string, expiresIn: string): Promise<User> {
    
    try {
      await this.setUser(
        username,
        accessToken,
        expiresIn
      );
      
      if (!this.user) {
        throw new Error('User not initialized');
      }
      
      ApiLocalStorage.set('user', this.user);
      
      const expirationTime = new Date().getTime() + 55 * 60 * 1000;
      ApiLocalStorage.set('session-expiration', expirationTime);
      
      this.events.triggerUserLoaded(this.user);
      
      return this.user;
      
    } catch (error) {
      console.error('Erro no loginOtp:', error);
      throw error;
    }
  }
  
  async loginOtpWithUserData(username: string, accessToken: string, expiresIn: string, userData: any): Promise<User> {
    
    try {
      this.user = {
        profile: {
          name: userData.fullname || userData.name || username,
          username,
          email: userData.email || '',
          fullname: userData.fullname || userData.name || username,
          phoneNumber: userData.phoneNumber || userData.phone || '',
          address_postalcode: userData.address_postalcode || '',
          address_street: userData.address_street || '',
          address_district: userData.address_district || '',
          address_number: userData.address_number || '',
          address_city: userData.address_city || '',
          address_state: userData.address_state || '',
          address_complement: userData.address_complement || '',
          address_reference: userData.address_reference || ''
        },
        access_token: accessToken,
        id_token: accessToken,
        expires_in: expiresIn,
        status: 'active',
        updated_at: new Date().toISOString()
      };
      
      ApiLocalStorage.set('user', this.user);
      
      const expirationTime = new Date().getTime() + 55 * 60 * 1000;
      ApiLocalStorage.set('session-expiration', expirationTime);
      
      this.events.triggerUserLoaded(this.user);
      
      return this.user;
      
    } catch (error) {
      console.error('Erro no loginOtpWithUserData:', error);
      throw error;
    }
  }
  
  async getUser(): Promise<User | null> {
    try {
      if (this.user) {
        return this.user;
      }
      
      const storedUser = ApiLocalStorage.get('user');
      
      if (storedUser) {
        this.user = storedUser;
        
        return this.user;
      }
      
      return null;
    } catch (error) {
      return null;
    }
  }
  
  async signoutRedirect(): Promise<void> {
    try {
      this.user = null;
      ApiLocalStorage.set('user', null);
      this.events.triggerUserUnloaded();
    } catch (error) {
      throw error;
    }
  }
}

export default new UserManager();
